const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const amountsOf = (detail, kind) => {
    const row =
        kind === "funding"
            ? detail?.fundingRequest || detail?.fr || detail
            : detail?.bill || detail;
    return {
        totalPayments: Number(row?.totalPayments || 0),
        totalDue: Number(row?.totalDue || 0),
        totalAdjustments: Number(row?.totalAdjustments || 0),
    };
};

const snapshot = (detail, kind) => {
    const a = amountsOf(detail, kind);
    return `${a.totalPayments}|${a.totalDue}|${a.totalAdjustments}`;
};

const paymentAmountOf = (result) =>
    Number(
        result?.summary?.paymentAmount ??
            result?.summary?.amount ??
            result?.hrp?.body?.paymentAmount ??
            0,
    );

const pollUntilChange = async ({
    queryClient,
    queryKey,
    fetchDetail,
    fetchArgs,
    kind,
    attempts = 8,
    delayMs = 900,
}) => {
    const before = snapshot(queryClient.getQueryData(queryKey), kind);
    for (let i = 0; i < attempts; i += 1) {
        await sleep(delayMs);
        const fresh = await fetchDetail(fetchArgs);
        queryClient.setQueryData(queryKey, fresh);
        if (snapshot(fresh, kind) !== before) return "synced";
    }
    return null;
};

const bumpOptimistic = (cur, kind, delta, field) => {
    if (!cur) return cur;
    const key = kind === "funding" ? (cur.fundingRequest ? "fundingRequest" : "fr") : "bill";
    const row = cur[key] || (kind === "funding" ? cur.fundingRequest : cur.bill);
    if (!row) return cur;
    const nextRow = {
        ...row,
        [field]: Number(row[field] || 0) + delta,
        totalDue:
            field === "totalPayments"
                ? Math.max(0, Number(row.totalDue || 0) - delta)
                : Number(row.totalDue || 0) + delta,
    };
    return { ...cur, [key]: nextRow, ...(cur.bill && key === "bill" ? { bill: nextRow } : {}) };
};

export const refreshBillAfterPayment = async ({
    queryClient,
    billFactKey,
    asOf,
    result,
    fetchDetail,
}) => {
    const queryKey = ["bill-run-detail", billFactKey, asOf || null];
    const status = await pollUntilChange({
        queryClient,
        queryKey,
        fetchDetail,
        fetchArgs: { billFactKey, asOfDate: asOf },
        kind: "premium",
    });
    if (status) {
        queryClient.invalidateQueries({ queryKey: ["bill-runs"] });
        return status;
    }
    const amount = paymentAmountOf(result);
    if (amount) {
        queryClient.setQueryData(queryKey, (cur) =>
            bumpOptimistic(cur, "premium", amount, "totalPayments"),
        );
    }
    queryClient.invalidateQueries({ queryKey: ["bill-runs"] });
    return "optimistic";
};

export const refreshBillAfterAdjustment = async ({
    queryClient,
    billFactKey,
    asOf,
    result,
    fetchDetail,
}) => {
    const queryKey = ["bill-run-detail", billFactKey, asOf || null];
    const status = await pollUntilChange({
        queryClient,
        queryKey,
        fetchDetail,
        fetchArgs: { billFactKey, asOfDate: asOf },
        kind: "premium",
    });
    if (status) {
        queryClient.invalidateQueries({ queryKey: ["bill-runs"] });
        return status;
    }
    const amount = Number(result?.summary?.amount || result?.summary?.adjustmentAmount || 0);
    if (amount) {
        queryClient.setQueryData(queryKey, (cur) =>
            bumpOptimistic(cur, "premium", amount, "totalAdjustments"),
        );
    }
    queryClient.invalidateQueries({ queryKey: ["bill-runs"] });
    return "optimistic";
};

export const refreshFundingAfterPayment = async ({
    queryClient,
    fundingRequestFactKey,
    asOf,
    result,
    fetchDetail,
}) => {
    const queryKey = ["funding-request-detail", fundingRequestFactKey, asOf || null];
    const status = await pollUntilChange({
        queryClient,
        queryKey,
        fetchDetail,
        fetchArgs: { fundingRequestFactKey, asOfDate: asOf },
        kind: "funding",
    });
    if (status) return status;
    const amount = paymentAmountOf(result);
    if (amount) {
        queryClient.setQueryData(queryKey, (cur) =>
            bumpOptimistic(cur, "funding", amount, "totalPayments"),
        );
    }
    return "optimistic";
};

export const refreshFundingAfterAdjustment = async ({
    queryClient,
    fundingRequestFactKey,
    asOf,
    result,
    fetchDetail,
}) => {
    const queryKey = ["funding-request-detail", fundingRequestFactKey, asOf || null];
    const status = await pollUntilChange({
        queryClient,
        queryKey,
        fetchDetail,
        fetchArgs: { fundingRequestFactKey, asOfDate: asOf },
        kind: "funding",
    });
    if (status) return status;
    const amount = Number(result?.summary?.amount || result?.summary?.adjustmentAmount || 0);
    if (amount) {
        queryClient.setQueryData(queryKey, (cur) =>
            bumpOptimistic(cur, "funding", amount, "totalAdjustments"),
        );
    }
    return "optimistic";
};
